import React, { useCallback, useState } from 'react'
import { Check, X, Scissors, HelpCircle, Scale, Sparkles, Loader2 } from 'lucide-react'
import { useToast } from '../ui/Toast'
import {
  acceptProposal,
  declineProposal,
  type AdmissionProposal,
  type AdmissionProposalKind,
} from '../../services/admissionService'
import { BRAND } from '../../config/brand'
import { AskEyebrow } from '../today/AskEyebrow'

// Per-kind presentation. `copy` from the backend is tone-guarded and rendered
// verbatim; this only controls the icon and the action verbs.
const KIND_META: Record<
  AdmissionProposalKind,
  { label: string; icon: React.ComponentType<{ className?: string }>; accept: string; decline: string }
> = {
  // `label` STATES A FACT; only `accept` / `decline` carry verbs. This zone sits
  // below "Start here", which the page's spine calls the one job, and the zone's
  // own rule says ignoring it breaks nothing — so an eyebrow reading "Break it
  // down" over a button reading "Break it down" both commanded and repeated
  // itself (owner, 2026-09-08: *"These are again mixed signals."*).
  decompose: { label: 'No steps yet', icon: Scissors, accept: 'Break it down', decline: 'Not now' },
  tradeoff: { label: 'Two ways to go', icon: Scale, accept: 'Do it', decline: 'Keep as is' },
  needs_information: { label: 'Needs a detail', icon: HelpCircle, accept: 'Sounds good', decline: 'Not now' },
  pursue_now: { label: 'Ready to pursue', icon: Sparkles, accept: 'Pursue now', decline: 'Later' },
  batch_release: { label: 'Ready to release', icon: Sparkles, accept: 'Release', decline: 'Hold' },
}

const fallbackMeta = KIND_META.needs_information

/**
 * What KIND of thing an ask is about, for the card's eyebrow.
 *
 * Owner, 2026-09-08, looking at an open ask: *"I am not able to determine if
 * this is a skill or a goal or a dream that I have to break it apart."* The
 * proposal has always carried `item_type`; the card simply never showed it, so
 * "Break it down" over a bare sentence could have been any of the three — and
 * the answer changes what breaking it down would even mean.
 */
const TYPE_WORD: Record<string, string> = {
  dream: 'Dream', goal: 'Goal', skill: 'Skill',
  habit: 'Habit', task: 'Task', action_item: 'Action item',
}

function typeWord(t: string | null | undefined): string | null {
  if (!t) return null
  return TYPE_WORD[t] ?? t.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())
}

/**
 * AdmissionInbox — surfaces open admission proposals (decompose offers,
 * trade-offs, missing-detail asks) so yanamas' "ask first" moments are
 * actually visible and actionable. Renders nothing when there are no open
 * proposals, so it's safe to mount at the top of any screen.
 */
/**
 * Presentational since 2026-09-08. The page fetches (`hooks/useAsks`) and
 * passes the proposals in, because the zone's "N to answer" has to be right
 * BEFORE the zone is expanded — and a collapsed zone never mounts its
 * children, so a card that fetches for itself has not fetched yet.
 */
export const AdmissionInbox: React.FC<{
  proposals: AdmissionProposal[]
  onResolved: () => void
}> = ({ proposals, onResolved }) => {
  const { success, error } = useToast()
  const [busyId, setBusyId] = useState<number | null>(null)

  const resolve = useCallback(
    async (p: AdmissionProposal, accept: boolean) => {
      setBusyId(p.id)
      const ok = accept ? await acceptProposal(p.id) : await declineProposal(p.id)
      setBusyId(null)
      if (!ok) {
        error("Couldn't update that just now", 'Please try again.')
        return
      }
      onResolved()
      if (accept) {
        success('Done', `${BRAND.assistant} will take it from here.`)
      } else {
        success('Saved for later', "It's parked on your life map — saved, not lost.")
      }
    },
    [success, error, onResolved],
  )

  if (proposals.length === 0) return null

  return (
    <div className="rounded-2xl border border-brand-subtle-border bg-surface p-4 shadow-sm">
      {/* No wrapper heading (2026-09-08). Inside the one ranked ask zone this
          card is a group of ROWS, and each row already names its own kind and
          the item it is about ("Break it down · Dream"). A heading here said
          that a second time — and said it wrong whenever the group held more
          than one kind, which it can: decompose, trade-off and needs-a-detail
          all arrive on this endpoint. */}
      <div className="mt-3 space-y-3">
        {proposals.map((p) => {
          const meta = KIND_META[p.kind] ?? fallbackMeta
          const Icon = meta.icon
          const busy = busyId === p.id
          return (
            <div key={p.id} className="p-4 rounded-2xl bg-surface-alt">
              <div className="flex items-start gap-3">
                <Icon className="w-4 h-4 mt-0.5 text-brand shrink-0" />
                <div className="flex-1 min-w-0">
                  <AskEyebrow label={meta.label} typeWord={typeWord(p.item_type)} />
                  {p.title && (
                    <div className="text-base font-bold text-primary mt-0.5" style={{ fontFamily: 'var(--font-display)' }}>
                      {p.title}
                    </div>
                  )}
                  <p className="text-sm leading-relaxed text-secondary mt-1">{p.copy}</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void resolve(p, true)}
                      className="min-h-touch rounded-xl bg-brand px-4 text-sm font-bold text-on-brand shadow-[0_4px_12px_rgba(120,81,169,0.3)] hover:bg-brand-hover disabled:opacity-60 transition-colors inline-flex items-center gap-1.5"
                    >
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      {meta.accept}
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void resolve(p, false)}
                      className="min-h-touch rounded-xl border border-brand-subtle-border bg-surface/70 px-4 text-sm text-secondary hover:bg-surface-alt disabled:opacity-60 transition-colors inline-flex items-center gap-1.5"
                    >
                      <X className="w-4 h-4" />
                      {meta.decline}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default AdmissionInbox
